/* eslint-disable import/no-named-as-default */
import { Form, Formik, FormikHelpers } from 'formik';
import { useRef } from 'react';

import useImportingToolUploadDocument, { ImportingToolUploadFields } from './hooks/useImportingToolUploadDocument';
import { IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import FileInput from '@/components/common/FileInput/FileInput';
import SmallText from '@/components/common/SmallText/SmallText';
import Button from '@/components/common/Button/Button';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';

import styles from './importing-tool-upload-form.module.scss';
import { ALLOWED_FILE_EXTENSIONS, validationSchema } from './validation/importingToolFormValidationSchema';
import { useGetImportingToolFiles } from '../FileUploadGrid/hooks/useGetImportingToolFiles';
import { useGetImportingToolTemplate } from '../FileUploadGrid/hooks/useGetImportingToolTemplate';

export default function ImportingToolFileUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { onFileUpload } = useImportingToolUploadDocument();
  const { getUploadedFiles } = useGetImportingToolFiles();
  const { getValue } = useAddQueryToUrl();
  const currentPage = parseInt(getValue('page') ?? '1', 10);
  const { getTemplate } = useGetImportingToolTemplate();

  const fileExtTip = `Only files of ${ALLOWED_FILE_EXTENSIONS.join(', ')}
    file ${FormattingUtils.pluralize(ALLOWED_FILE_EXTENSIONS.length, 'format')} can be uploaded`;

  const onSubmit =
    async (values: ImportingToolUploadFields, formikHelpers: FormikHelpers<ImportingToolUploadFields>) => {
      const successCallback = () => {
        formikHelpers.resetForm();
        formikHelpers.validateForm();
        getUploadedFiles(currentPage, IMPORTING_TOOL_UPLOADED_FILES_PER_PAGE);

        if (fileInputRef.current?.value) {
          fileInputRef.current.value = '';
        }
      };

      onFileUpload({ values, successCallback });
    };

  return (
    <>
      <Formik<ImportingToolUploadFields>
        initialValues={{
          file: undefined
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={onSubmit}
      >
        {({ isValid, setFieldValue, setFieldTouched, errors, touched }) => (
          <Form>
            <div className={styles.form}>
              <label
                className={styles.label}
                htmlFor="file"
              >
                Upload File
              </label>

              <div className={styles['input-wrapper']}>
                <FileInput
                  ref={fileInputRef}
                  id="file"
                  name="file"
                  format="text/csv"
                  onChange={(e) => {
                    setFieldValue('file', e.target?.files?.[0]);
                    setFieldTouched('file', true, false);
                  }}
                />

                {errors.file && touched.file && (
                  <ValidationAlert>
                    {errors.file}
                  </ValidationAlert>
                )}

                <SmallText className={styles.small}>{fileExtTip}</SmallText>
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={!isValid}
                >
                  Upload
                </Button>

              </div>

              <div className={styles['sample-link']}>
                <span>
                  Click <a style={{ fontSize: '16px' }} onClick={getTemplate}>here</a> for a sample template/format
                </span>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
